/* ManageConnectionsComponent.js */

"use strict";

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ManageConnectionsRenderer");
const MAX_PAGE_SIZE = 200;

/**
 * Class representing the Manage Connections Component.
 *
 * @fires ManageConnectionsComponent#OrganizationChanged
 * @fires ManageConnectionsComponent#feedUpdated
 */
class ManageConnectionsComponent extends EventEmitter {
    /**
     * Creates an instance of ManageConnectionsComponent.
     *
     * @param {UserSession} userSession - Session object for the current user.
     * @param {CoreLogger} logger - Logging utility for warnings, errors, and information.
     * @param {UserFeedback} userFeedback - User feedback system for displaying veils to users.
     * @param {ServiceConnectionType} ServiceConnectionType - Entity for managing connection types to external services.
     * @param {ServiceConnection} ServiceConnection - Entity for managing service connections to external services.
     * @param {JnibAdapter} jnibAdapter - Adapter for interacting with the JNIB API service.
     */
    constructor(
        userSession,
        logger,
        userFeedback,
        ServiceConnectionType,
        ServiceConnection,
        jnibAdapter,
        uiUtils
    ) {
        super();
        this._userSession = userSession;
        this._ServiceConnectionTypeEntityDef = ServiceConnectionType;
        this._ServiceConnectionEntityDef = ServiceConnection;
        this._nibApiService = jnibAdapter.getNibApiService();
        this._logger = logger;
        this._modalUserFeedback = userFeedback;
        this._slickFormatData = undefined;
        this.serviceConnectionsFeed = undefined;
        this._renderer = new Renderer(this);
        this._uiUtils = uiUtils;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerFeedListeners() {
        this.serviceConnectionsFeed.on("EntityAdded", this.feedUpdated.bind(this));
        this.serviceConnectionsFeed.on("EntityPropertiesUpdated", this.feedUpdated.bind(this));
        this.serviceConnectionsFeed.on("EntityRemoved", this.feedUpdated.bind(this));
    }

    _unregisterFeedListeners() {
        this.serviceConnectionsFeed.removeListener("EntityAdded", this.feedUpdated);
        this.serviceConnectionsFeed.removeListener("EntityPropertiesUpdated", this.feedUpdated);
        this.serviceConnectionsFeed.removeListener("EntityRemoved", this.feedUpdated);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    get slickData() {
        return this.getSlickData();
    }

    getSlickData() {
        return this._slickFormatData;
    }

    get userFeedback() {
        return this.getUserFeedback();
    }

    getUserFeedback() {
        return this._modalUserFeedback;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get userSession() {
        return this.getUserSession();
    }

    getUserSession() {
        return this._userSession;
    }

    get logger() {
        return this.getLogger();
    }

    getLogger() {
        return this._logger;
    }

    get slickFormatData() {
        return this.getSlickFormatData();
    }

    getSlickFormatData() {
        return this._slickFormatData;
    }

    async whenModelIsReady() {
        this._serviceConnectionTypes = await this._loadServiceConnectionTypes();
        return await this._getServiceConnectionFeed();
    }

    shutdown() {
        this._unregisterFeedListeners();
        return this._renderer
            ._shutdown()
            .then(() => this.serviceConnectionsFeed.shutdown())
            .then(
                () => {
                    [
                        "_userSession",
                        "_ServiceConnectionTypeEntityDef",
                        "_ServiceConnectionEntityDef",
                        "_logger",
                        "_modalUserFeedback",
                        "_slickFormatData",
                        "serviceConnectionsFeed",
                        "_renderer",
                    ].forEach((prop) => (this[prop] = undefined));
                },
                (err) => {
                    this._logger.warn(
                        {error: err},
                        "ManageConnectionsComponent.shutdown() Error shutting down ManageConnectionsComponent"
                    );
                }
            );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    async organizationChanged() {
        /**
         * Emitted when the active organization changes.
         * @event ManageConnectionsComponent#OrganizationChanged
         */
        try {
            this.emit("OrganizationChanged"); // Emit event when org is changed
        } catch (err) {
            this._logger.warn(err, "ManageConnectionsComponent.organizationChanged() error.");
        }
    }

    feedUpdated() {
        /**
         * Emitted when the active organization changes.
         * @event ManageConnectionsComponent#feedUpdated
         */
        this._convertConnectionDataToSlickTableFormat(this.serviceConnectionsFeed.cache || {});
        this.emit("feedUpdated");
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _loadServiceConnectionTypes() {
        return await this._ServiceConnectionTypeEntityDef.loadServiceConnectionTypes(
            this.activeOrgId,
            {},
            {},
            {displayName: 1, "properties.metadata.displayName": 1, name: 1}
        );
    }

    async _getServiceConnectionFeed() {
        try {
            if (_.isEmpty(this.serviceConnectionsFeed)) {
                this.serviceConnectionsFeed =
                    this._ServiceConnectionEntityDef.startAscendServiceConnectionsFeed(
                        this.activeOrgId,
                        {},
                        {},
                        {},
                        {skip: 0, limit: MAX_PAGE_SIZE}
                    );
            } else {
                this._unregisterFeedListeners();
                this.serviceConnectionsFeed.reestablishFeed(this.activeOrgId);
            }

            await this.serviceConnectionsFeed.whenInitialized();
            this._convertConnectionDataToSlickTableFormat(this.serviceConnectionsFeed.cache || {});
            this._registerFeedListeners();
            return Promise.resolve();
        } catch (error) {
            this._logger.error({error}, "Error initializing service connections feed");
            throw error;
        }
    }

    async _retrieveConnection(id) {
        if (!id) {
            return Promise.resolve(null);
        }
        return this._ServiceConnectionEntityDef.loadServiceConnection(this.activeOrgId, id, {});
    }

    async deleteConnection(id) {
        try {
            const entity = await this._retrieveConnection(id);
            return await entity.delete();
        } catch (error) {
            this._logger.error({error}, `Failed to delete connection with ID: ${id}`);
            throw error;
        }
    }

    _getServiceConnectionTypeDisplayName(id) {
        if (_.isEmpty(this._serviceConnectionTypes) || !id) {
            return "";
        }

        const connectionType = this._serviceConnectionTypes.find((item) => item.id === id);

        return _.get(connectionType, "pristineProperties.metadata.displayName", "");
    }

    _convertConnectionDataToSlickTableFormat(connectionData = {}) {
        this._slickFormatData = Object.entries(connectionData).map(([id, connection]) => ({
            id: id,
            name: _.get(connection, "pristineProperties.name", ""),
            description: _.get(connection, "pristineProperties.description", ""),
            serviceConnectionTypeDisplayName: this._getServiceConnectionTypeDisplayName(
                _.get(connection, "pristineProperties.connectionTypeId")
            ),
            isInherited: _.get(connection, "tenantId", this.activeOrgId) !== this.activeOrgId,
            isSystemManaged: _.get(connection, "pristineProperties.metadata.source") === "gluware",
            shared: _.get(connection, "pristineProperties.shared", false),
        }));
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = ManageConnectionsComponent;
ManageConnectionsComponent.$groupName = "Organization";
ManageConnectionsComponent.$displayName = "Connections";
ManageConnectionsComponent.$inject = [
    "$teve.userSession",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.entities.ServiceConnectionType",
    "$teve.entities.ServiceConnection",
    "$teve.jnibAdapter",
    "$teve.ui.UiUtils",
];
